import { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@/lib/partner-client";

export function CredentialsPage() {
  const clientId = useSession((s) => s.clientId);
  const scope = useSession((s) => s.scope);
  const expiresAt = useSession((s) => s.expiresAt);
  const [copied, setCopied] = useState(false);

  function copy(value: string) {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const tokenExpiresIn = Math.max(
    0,
    Math.floor((expiresAt - Date.now()) / 1000 / 60),
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/" className="text-sm text-neutral-500 hover:underline">
        ← Dashboard
      </Link>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">Credentials</h1>

      <section className="mt-8 rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
        <h2 className="text-sm font-medium text-neutral-500">Client ID</h2>
        <div className="mt-2 flex items-center gap-3">
          <code className="flex-1 rounded bg-neutral-100 px-3 py-2 font-mono text-sm dark:bg-neutral-900">
            {clientId ?? "—"}
          </code>
          <button
            onClick={() => clientId && copy(clientId)}
            className="rounded-md bg-neutral-900 px-3 py-2 text-xs text-white hover:bg-neutral-700 dark:bg-neutral-100 dark:text-neutral-900"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <p className="mt-3 text-xs text-neutral-500">
          Include in every SDK init as <code>APPSUMO_CLIENT_ID</code>.
        </p>
      </section>

      <section className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
        <h2 className="font-medium">Client secret</h2>
        <p className="mt-2">
          Your secret was shown <strong>once</strong> in your onboarding email
          and is never displayed here. If you've lost it, contact your AppSumo
          partner manager to rotate.
        </p>
      </section>

      <section className="mt-6 rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
        <h2 className="text-sm font-medium text-neutral-500">Webhook signing secret</h2>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Used to verify <code>Hub-Signature</code> on requests the Hub
          dispatches to your endpoint. Rotate it via{" "}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-900">
            POST /v1/partner/me/signing-secret/rotate
          </code>
          . Rotation reveals the new secret once; the previous remains valid
          for 24 hours to avoid signed-request gaps.
        </p>
        <p className="mt-3 text-xs text-neutral-500">
          <em>Rotation UI lands in M28h.</em>
        </p>
      </section>

      <section className="mt-6 rounded-xl border border-neutral-200 bg-white p-6 text-sm dark:border-neutral-800 dark:bg-neutral-950">
        <h2 className="font-medium text-neutral-500">Current session</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <dt className="text-neutral-500">Token expires in</dt>
          <dd className="text-right">
            {tokenExpiresIn > 0 ? `${tokenExpiresIn} min` : "expired"}
          </dd>
          <dt className="text-neutral-500">Scope</dt>
          <dd className="truncate text-right font-mono">{scope ?? "—"}</dd>
        </dl>
      </section>
    </div>
  );
}
